import type {
  ExtensionDiagnostic,
  GenesisExtensionManifest,
} from './types'

const ID_PATTERN =
  /^[a-z0-9][a-z0-9._-]{2,80}$/

class GenesisExtensionRegistry {
  private manifests =
    new Map<
      string,
      GenesisExtensionManifest
    >()

  private diagnostics:
    ExtensionDiagnostic[] = []

  register(
    candidate: unknown,
  ) {
    if (
      !candidate ||
      typeof candidate !== 'object'
    ) {
      this.reject(
        'unknown',
        'Manifest is not an object',
      )

      return
    }

    const m =
      candidate as
        Partial<GenesisExtensionManifest>

    if (
      typeof m.id !== 'string' ||
      !ID_PATTERN.test(m.id)
    ) {
      this.reject(
        String(m.id ?? 'unknown'),
        'Invalid extension id',
      )

      return
    }

    if (
      typeof m.name !== 'string' ||
      typeof m.description !== 'string' ||
      typeof m.version !== 'string'
    ) {
      this.reject(
        m.id,
        'Missing metadata',
      )

      return
    }

    if (m.apiVersion !== '1') {
      this.reject(
        m.id,
        'Unsupported manifest API',
      )

      return
    }

    if (
      m.kind !== 'MODULE' &&
      m.kind !== 'WIDGET'
    ) {
      this.reject(
        m.id,
        'Unsupported extension kind',
      )

      return
    }

    if (
      typeof m.component !== 'function'
    ) {
      this.reject(
        m.id,
        'Missing React component',
      )

      return
    }

    if (
      this.manifests.has(m.id)
    ) {
      this.reject(
        m.id,
        'Duplicate extension id',
      )

      return
    }

    this.manifests.set(
      m.id,
      m as GenesisExtensionManifest,
    )

    this.diagnostics.push({
      id: m.id,
      status:
        m.enabledByDefault
          ? 'ACTIVE'
          : 'DISABLED',
    })
  }

  active() {
    return [
      ...this.manifests.values(),
    ]
      .filter(
        item =>
          item.enabledByDefault,
      )
      .sort(
        (a, b) =>
          a.priority - b.priority,
      )
  }

  modules() {
    return this.active()
      .filter(
        item =>
          item.kind === 'MODULE',
      )
  }

  widgets() {
    return this.active()
      .filter(
        item =>
          item.kind === 'WIDGET',
      )
  }

  report() {
    return [
      ...this.diagnostics,
    ]
  }

  private reject(
    id: string,
    reason: string,
  ) {
    this.diagnostics.push({
      id,
      status: 'REJECTED',
      reason,
    })
  }
}

export const extensionRegistry =
  new GenesisExtensionRegistry()
